import { useSubscribeToBlogMutation, useUnsubscribeToBlogMutation } from '@blog/services/blog';
import { useAuth } from '@hooks/useAuth';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, FormControl, IconButton, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { blogSubscriptionSchema } from 'src/features/blog/schemas/BlogSchema';
interface Props {
    open: boolean;
    subscribed: boolean | undefined;
    onClose?: () => void;
}

export const SubscribeModal = ({ open, subscribed, onClose }: Props) => {
    const [subscribeToBlog, { isSuccess: isSubscribeSuccess, isError: isSubscribeError }] =
        useSubscribeToBlogMutation();
    const [unsubscribeToBlog, { isSuccess: isUnsubscribeSuccess, isError: isUnsubscribeError }] =
        useUnsubscribeToBlogMutation();

    const { user } = useAuth();
    const intl = useIntl();

    const showSubscribeForm = !subscribed && !isUnsubscribeSuccess && !isSubscribeError;
    const showUnsubscribeForm = subscribed && !isSubscribeSuccess && !isUnsubscribeError;

    const showSubscribeSuccess = subscribed && isSubscribeSuccess && !isSubscribeError;
    const showUnsubscribeSuccess = !subscribed && isUnsubscribeSuccess && !isUnsubscribeError;

    const showError = useMemo(
        () => (isSubscribeError && !isSubscribeSuccess) || (isUnsubscribeError && !isUnsubscribeSuccess),
        [isSubscribeError, isSubscribeSuccess, isUnsubscribeError, isUnsubscribeSuccess]
    );

    const {
        values,
        touched,
        errors,
        handleSubmit: handleSubscribe,
        handleChange,
    } = useFormik<{ email: string }>({
        initialValues: { email: user?.email ?? '' },
        onSubmit: async (values) => {
            const { email } = values;
            try {
                await subscribeToBlog(email).unwrap();
            } catch (e) {
                console.log('error', e);
            }
        },
        validationSchema: blogSubscriptionSchema,
    });

    const handleUnsubscribe = useCallback(() => {
        if (user) {
            unsubscribeToBlog(user.email);
        }
    }, [user, unsubscribeToBlog]);

    const handleOutsideClose = useCallback(
        (e: object, reason: string) => {
            if (reason === 'backdropClick') {
                return;
            }
            onClose?.();
        },
        [onClose]
    );

    return (
        <Dialog
            disableEscapeKeyDown
            open={open}
            onClose={handleOutsideClose}
            className="mx-auto lg:w-[1200px]"
            sx={{ '& .MuiDialog-container': { width: '100%' } }}
            slotProps={{
                paper: { className: 'relative px-8 pb-4 pt-12 rounded-3xl' },
            }}
        >
            <>
                <IconButton className="absolute top-2 right-2" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
                {showSubscribeForm && (
                    <>
                        <div className="relative">
                            <Typography variant="h4" className="font-museo text-center">
                                <FormattedMessage id="blog.stayUpToDate" defaultMessage="Stay up to date" />
                            </Typography>
                        </div>

                        <div className="px-4 pb-6">
                            <Typography className="font-inter text-center text-base mt-4 tracking-wide">
                                <FormattedMessage
                                    id="blog.subscribeDescription"
                                    defaultMessage="Fill the fields below and keep up to date with our blog"
                                />
                            </Typography>
                            <FormControl
                                component={'form'}
                                onSubmit={handleSubscribe}
                                className="flex flex-col gap-4 w-full mt-10"
                            >
                                <TextField
                                    id="email"
                                    name="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    error={touched.email && Boolean(errors.email)}
                                    helperText={touched.email && errors.email}
                                    placeholder={intl.formatMessage({ id: 'common.email', defaultMessage: 'Email' })}
                                    className="w-full"
                                />

                                <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-6 mt-10">
                                    <Button color="neutral-700" className="grow rounded-full" onClick={onClose}>
                                        <ArrowBackIcon className="mr-2" />
                                        <FormattedMessage id="common.goBack" defaultMessage="Go Back" />
                                    </Button>
                                    <Button className="grow rounded-full" type="submit">
                                        <CheckCircleOutlineOutlinedIcon className="mr-2" />
                                        <FormattedMessage id="common.confirm" defaultMessage="Confirm" />
                                    </Button>
                                </div>
                            </FormControl>
                        </div>
                    </>
                )}
                {showUnsubscribeForm && (
                    <>
                        <div className="relative">
                            <Typography variant="h4" className="font-museo text-center">
                                <FormattedMessage
                                    id="blog.unsubscribeConfirmation"
                                    defaultMessage="Are you sure you want to unsubscribe?"
                                />
                            </Typography>
                            <div className="px-4 pb-6">
                                <div className="sm:max-w-[60%] m-auto mt-4">
                                    <Typography className="font-inter text-center text-base tracking-wide mt-4 tracking-wide">
                                        <FormattedMessage
                                            id="blog.unsubscribeDescription"
                                            defaultMessage="By clicking unsubscribe you will no longer received email updates regarding the
                                            Victron Blog."
                                        />
                                    </Typography>
                                </div>

                                <div className="flex flex-col gap-4 w-full mt-2">
                                    <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-6 mt-10">
                                        <Button color="neutral-700" className="grow rounded-full" onClick={onClose}>
                                            <ArrowBackIcon className="mr-2" />
                                            <FormattedMessage id="common.goBack" defaultMessage="Go Back" />
                                        </Button>
                                        <Button
                                            color="error-500"
                                            className="grow rounded-full"
                                            onClick={handleUnsubscribe}
                                        >
                                            <ClearIcon className="mr-2" />
                                            <FormattedMessage id="common.unsubscribe" defaultMessage="Unsubscribe" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                {showSubscribeSuccess && (
                    <div className="sm:w-[80%] my-12 mx-auto">
                        <div className="flex border-2 border-solid rounded-full w-20 h-20 mx-auto">
                            <CheckIcon className="text-5xl block m-auto" />
                        </div>
                        <Typography className="font-museo text-5xl text-bold text-center my-10">
                            <FormattedMessage id="common.success" defaultMessage="Success" />
                        </Typography>
                        <Typography className="font-museo text-2xl text-center tracking-wide">
                            <FormattedMessage
                                id="blog.subscribeThankYou"
                                defaultMessage="Thank you for subscribing to our blog"
                            />
                        </Typography>
                    </div>
                )}
                {showUnsubscribeSuccess && (
                    <div className="sm:w-[80%] my-12 mx-auto">
                        <div className="flex border-2 border-solid rounded-full w-20 h-20 mx-auto">
                            <CheckIcon className="text-5xl block m-auto" />
                        </div>
                        <Typography className="font-museo text-5xl text-bold text-center my-10">
                            <FormattedMessage id="common.success" defaultMessage="Success" />
                        </Typography>
                        <Typography className="font-museo text-2xl text-center tracking-wide">
                            <FormattedMessage
                                id="blog.unsubscribeSuccessDescription"
                                defaultMessage="You have successfully unsubscribed from our blog"
                            />
                        </Typography>
                    </div>
                )}
                {showError && (
                    <div className="sm:w-full my-12 mx-auto">
                        <div className="flex border-2 border-solid rounded-full w-20 h-20 mx-auto">
                            <CloseIcon className="text-5xl block m-auto" />
                        </div>
                        <Typography className="font-museo text-5xl text-bold text-center my-10">
                            <FormattedMessage id="blog.subscriptionError" defaultMessage="Subscription Error" />
                        </Typography>
                        <Typography className="font-museo text-2xl text-center tracking-wide">
                            <FormattedMessage
                                id="blog.subscriptionErrorDescription"
                                defaultMessage="A subscription error occured."
                            />
                        </Typography>
                        <Typography className="font-museo text-2xl text-center tracking-wide">
                            <FormattedMessage id="common.pleaseTryAgain" defaultMessage="Please try again later." />
                        </Typography>
                    </div>
                )}
            </>
        </Dialog>
    );
};
